import React, { ReactElement, useEffect, useState } from 'react';
import css from './AdditionalServices.module.scss';
import '../../css/themes.scss';
import AdditionalService from 'src/components/AdditionalServices/AdditionalService/containers/AdditionalService';
import { IView } from 'Framework';
import Footer from '../Footer/Footer';
import { ComponentMapIds } from '../map';
import { IServicesState } from 'src/store/states';
import { ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';
import { ApptVeh, SpecData, VehicleRec, ascCtaInteractionInput, ascPageviewInput } from 'src/models';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import { getProviderId, getSdMileage, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { THEME } from 'src/constants/ThemeNames';

export interface IProps {
    inquiryInvoked: boolean;
    inquiryReturned: boolean;
    inquiryError: boolean;
    showWeOwes?: boolean;
    showOEMRecall?: boolean;
    showPrvRecom?: boolean;
    additionalServices: IServicesState;
    specDefaultDept: string;
    skipServicesFlag?: boolean;
    vehicleSpecs?: SpecData;
    skipServices: boolean;
    apptDept: string;
    lexusMCLaunched: string,
    toyotaMCLaunched: string,
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    locale: string,
    mainPaneViewOrder: IView[];
    theme?: string;
    countryCode: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    sessionId: string;
    specData?: SpecData;
    apptVeh: ApptVeh;
    pushToMainPanes: (inputView: IView) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    getDeptSpecs: () => void;
}

const AdditionalServices = (props: IProps) => {
    const [skipLit, setSkipLit] = useState<boolean>(props.skipServicesFlag ? true:false);

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';

    let weOwes: ReactElement | null = null;
    if (props.showWeOwes && props.additionalServices.allServices.weOwes && props.additionalServices.allServices.weOwes.length > 0) {
        weOwes = <AdditionalService type='WeOwe' ></AdditionalService>;
    }
    let recommended: ReactElement | null = null;
    if (props.showPrvRecom && props.additionalServices.allServices.recom && props.additionalServices.allServices.recom.length > 0) {
        recommended = <AdditionalService type='Recommended'></AdditionalService>;
    }
    let recalls: ReactElement | null = null;
    if (props.showOEMRecall && ((props.inquiryInvoked && !props.inquiryReturned) || (props.inquiryReturned))) {
        recalls = <AdditionalService type='Recall'></AdditionalService>;
    }
    let maintContracts: ReactElement | null = null;
    const maintMake = props.selectedVehicle && props.selectedVehicle !== 'AddVehicle' ?
        props.vehicles[props.selectedVehicle].make.toLowerCase().split(' ')[0]:'';
    // if (props.selectedVehicle && props.locale === 'en-US' &&
    if (props.selectedVehicle && props.countryCode === 'US' &&
        (((props.lexusMCLaunched || props.lexusTDMCLaunched) && maintMake==='lexus') ||
        ((props.toyotaMCLaunched || props.toyotaTDMCLaunched) && maintMake==='toyota'))) {
        maintContracts = <AdditionalService type='Maintenance Contract'></AdditionalService>;
    }

    useEffect(() => {
        if (props.additionalServices.checkedWeOwes.length === 0 && props.additionalServices.checkedRecalls.length === 0 &&
            props.additionalServices.checkedRecoms.length === 0) {
            setSkipLit(true);
        } else {
            setSkipLit(false);
        }
    }, [props.additionalServices, props.skipServicesFlag]);

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
        if (currentPane.viewId === ComponentMapIds.AdditionalServices) {
            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceSelectionShown', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceVehicleMake: props.apptVeh.make,
                    scheduleServiceVehicleMileage: getSdMileage(props.apptVeh.mileage),
                    scheduleServiceVehicleModel: props.apptVeh.model,
                    scheduleServiceVehicleVin: props.apptVeh.vin,
                    scheduleServiceVehicleYear: props.apptVeh.vehYr,
                });
            }
        }
    // eslint-disable-next-line @reynolds/exhaustive-deps
    }, [props.mainPaneViewOrder]);

    const weOwesLength = props.additionalServices.allServices.weOwes?.length ?? 0;
    const recomLength = props.additionalServices.allServices.recom?.length ?? 0;
    const recallsLength = props.additionalServices.recallData?.recalls?.length ?? 0;
    const maintContractLength = props.additionalServices.maintContractData?.maintContracts?.length ?? 0;

    useEffect(() => {
        if (props.additionalServices.allServices.built &&
            (props.additionalServices.recallData.inquiryReturned || !props.inquiryInvoked) &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.AdditionalServices) {
            const itemResults = weOwesLength +
                recomLength+
                recallsLength +
                maintContractLength;
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: itemResults.toString(),
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [weOwesLength,
        recomLength,
        recallsLength,
        maintContractLength,
        props.additionalServices.allServices.built,
        // props.additionalServices.maintContractData.MCReturned,
        props.additionalServices.recallData.inquiryReturned,
        props.inquiryInvoked,
        props.mainPaneViewOrder,
        props.vehicles,
        props.selectedVehicle]);

    const recomTimes = (
        {
            'S': props.vehicleSpecs?.svcSpecs?.recommTimes,
            'B': props.vehicleSpecs?.bdySpecs?.recommTimes,
            'Q': props.vehicleSpecs?.ossSpecs?.recommTimes,
        }
    );

    // Proceed to the Service Tab screen
    const footerHandler = (e: any) => {
        if (props.skipServices) {
            props.getDeptSpecs();
            if (recomTimes[props.apptDept] === 'Y') {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.QuickTime,
                    component: ComponentMapIds.QuickTime,
                });
            } else {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.CalendarSelect,
                    component: ComponentMapIds.CalendarSelect,
                });
            }
        } else {
            props.pushToMainPanes({
                viewId: ComponentMapIds.ServiceTab,
                component: ComponentMapIds.ServiceTab,
            });
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: skipLit ? strings.Skip : strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    };

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Additional Services', '');
    }, [
        props.addLogEntry,
    ]);

    const bodyCss = props.theme === THEME.AUDI ? css.bodyAudi : css.body;

    return (
        <div>
            <div className={bodyCss}>
                <ScrollBar scrollVertical>
                    <div>
                        {weOwes}
                        {recalls}
                        {maintContracts}
                        {recommended}
                    </div>
                </ScrollBar>
            </div>
            <Footer
                id='AddSvcsFooter'
                handler={footerHandler}
                message={skipLit ? strings.Skip : strings.Proceed}
                secondaryhandler={footerHandler}
                theme={props.theme}
            />
        </div>
    );
};

export default AdditionalServices;